/**
 * Client file-gathering for HTML site uploads.
 *
 * Turns a dropped folder, a directory <input>, or a dropped .zip into a flat
 * list of { path, file } with web-root-relative paths, ready for the
 * store-once deploy service. No network access here — pure local processing.
 *
 * Three input shapes are supported:
 *   1. Folder drag-drop  — DataTransfer items via webkitGetAsEntry, recursing
 *      subdirectories and looping readEntries (Chromium caps at 100 per call).
 *   2. Directory <input type="file" webkitdirectory> — File.webkitRelativePath.
 *   3. A dropped .zip     — expanded in-browser with fflate.
 */
import { unzip } from 'fflate';

// ---------------------------------------------------------------------------
// Path helpers / junk filtering
// ---------------------------------------------------------------------------

// Normalize a raw relative path: forward slashes, no leading "./" or "/",
// collapse duplicate slashes.
function normalizeRel(p) {
  return String(p || '')
    .replace(/\\/g, '/')
    .replace(/^\.?\/+/, '')
    .replace(/\/+/g, '/');
}

function basename(p) {
  const parts = normalizeRel(p).split('/');
  return parts[parts.length - 1] || '';
}

// Drop OS / archive cruft: anything under a __MACOSX segment, .DS_Store,
// Thumbs.db, AppleDouble "._*" files, and any dotfile basename.
function isJunk(path) {
  const segments = normalizeRel(path).split('/');
  if (segments.some((s) => s === '__MACOSX')) return true;
  const name = segments[segments.length - 1] || '';
  if (name === '.DS_Store' || name === 'Thumbs.db') return true;
  if (name.startsWith('._')) return true;
  if (name.startsWith('.')) return true;
  return false;
}

// ---------------------------------------------------------------------------
// FileSystemEntry traversal (drag-drop)
// ---------------------------------------------------------------------------

// Read EVERY entry from a directory reader. readEntries returns at most 100
// entries per call in Chromium, so we loop until it returns an empty batch.
function readAllEntries(reader) {
  return new Promise((resolve, reject) => {
    const all = [];
    const readBatch = () => {
      reader.readEntries((batch) => {
        if (!batch.length) {
          resolve(all);
          return;
        }
        all.push(...batch);
        readBatch();
      }, reject);
    };
    readBatch();
  });
}

function entryToFile(entry) {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}

// Recursively walk a FileSystemEntry, pushing { path, file } into out. basePath
// is the accumulated path of the parent directory (with a trailing slash).
async function traverseEntry(entry, basePath, out) {
  if (entry.isFile) {
    const file = await entryToFile(entry);
    out.push({ path: normalizeRel(basePath + entry.name), file });
    return;
  }
  if (entry.isDirectory) {
    const reader = entry.createReader();
    const children = await readAllEntries(reader);
    const dirPath = basePath + entry.name + '/';
    for (const child of children) {
      await traverseEntry(child, dirPath, out);
    }
  }
}

// ---------------------------------------------------------------------------
// react-dropzone getFilesFromEvent
// ---------------------------------------------------------------------------

/**
 * Custom getFilesFromEvent for react-dropzone. Must run synchronously enough to
 * capture webkitGetAsEntry handles before the DataTransfer is neutered, so we
 * grab all entry handles first, then traverse them. Returns an array of File
 * objects each carrying a `.path` property (matching react-dropzone's own
 * convention) so onDrop can read the web-root-relative path.
 */
export async function getFilesFromEvent(event) {
  const items =
    event && event.dataTransfer && event.dataTransfer.items
      ? event.dataTransfer.items
      : null;

  if (items && items.length && typeof items[0].webkitGetAsEntry === 'function') {
    // Grab every entry handle synchronously before any await.
    const entries = [];
    for (let i = 0; i < items.length; i++) {
      const entry = items[i].webkitGetAsEntry();
      if (entry) entries.push(entry);
    }
    const collected = [];
    for (const entry of entries) {
      await traverseEntry(entry, '', collected);
    }
    return collected.map(({ path, file }) => {
      try {
        file.path = path;
      } catch (e) {
        /* File.path may be read-only in some engines; path is read below too */
      }
      return file;
    });
  }

  // Fallback: directory <input> (webkitRelativePath) or a plain file drop.
  const fileList =
    event && event.target && event.target.files
      ? event.target.files
      : event && event.dataTransfer && event.dataTransfer.files
      ? event.dataTransfer.files
      : [];

  const out = [];
  for (let i = 0; i < fileList.length; i++) {
    const file = fileList[i];
    const rel = file.webkitRelativePath || file.path || file.name;
    try {
      file.path = normalizeRel(rel);
    } catch (e) {
      /* ignore */
    }
    out.push(file);
  }
  return out;
}

// ---------------------------------------------------------------------------
// .zip expansion (fflate)
// ---------------------------------------------------------------------------

function isZip(path, file) {
  if (/\.zip$/i.test(path || '')) return true;
  const type = file && file.type;
  return type === 'application/zip' || type === 'application/x-zip-compressed';
}

function unzipAsync(u8) {
  return new Promise((resolve, reject) => {
    unzip(u8, (err, data) => (err ? reject(err) : resolve(data)));
  });
}

async function expandZip(file) {
  const u8 = new Uint8Array(await file.arrayBuffer());
  const data = await unzipAsync(u8);
  const out = [];
  for (const name of Object.keys(data)) {
    if (name.endsWith('/')) continue; // directory marker
    const bytes = data[name];
    const blob = new File([bytes], basename(name), { type: '' });
    out.push({ path: normalizeRel(name), file: blob });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Web-root detection + homepage
// ---------------------------------------------------------------------------

// If every file shares one common top-level folder, strip it so index.html
// lands at the web root. If any file already sits at the root, do nothing.
function stripCommonRoot(entries) {
  if (entries.length === 0) return entries;
  const tops = new Set();
  for (const e of entries) {
    const idx = e.path.indexOf('/');
    if (idx === -1) return entries; // a root-level file — no wrapper to strip
    tops.add(e.path.slice(0, idx));
  }
  if (tops.size !== 1) return entries;
  const prefix = [...tops][0] + '/';
  return entries.map((e) => ({ path: e.path.slice(prefix.length), file: e.file }));
}

// index.html at the root wins; otherwise the first top-level .html file.
// Falls back to 'index.html' when there is no top-level HTML at all.
function detectHomePage(entries) {
  const roots = entries.filter((e) => !e.path.includes('/'));
  if (roots.some((e) => e.path.toLowerCase() === 'index.html')) return 'index.html';
  const firstHtml = roots.find((e) => /\.html?$/i.test(e.path));
  return firstHtml ? firstHtml.path : 'index.html';
}

// ---------------------------------------------------------------------------
// Public entry point
// ---------------------------------------------------------------------------

/**
 * Given the File[] react-dropzone produced (each with a `.path`), produce the
 * final deploy payload: { files: [{ path, file }], homePage }.
 * Expands any .zip, filters junk, strips a common root folder, and detects the
 * homepage.
 */
export async function gatherFiles(rawFiles) {
  let entries = [];
  for (const file of rawFiles) {
    const path = normalizeRel(file.path || file.webkitRelativePath || file.name);
    if (isZip(path, file)) {
      const expanded = await expandZip(file);
      entries.push(...expanded);
    } else {
      entries.push({ path, file });
    }
  }

  entries = entries.filter((e) => e.path && !isJunk(e.path));
  entries = stripCommonRoot(entries);
  const homePage = detectHomePage(entries);

  return { files: entries, homePage };
}
